import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { useState } from 'react'
import { FaExternalLinkAlt, FaGithub } from 'react-icons/fa'
import styled from 'styled-components'

import { addViews, api } from '../util'

export const Skeleton = styled.div`
  animation: skeletonAnimation 1s ease-in-out infinite alternate;

  @keyframes skeletonAnimation {
    0% {
      opacity: 0.6;
      background-color: #e6e6e6;
    }
    100% {
      opacity: 1;
      background-color: #171717;
    }
  }
`

const A = styled.div`
  position: relative;
  text-decoration: none;
  color: inherit;
  &:hover {
    text-decoration: none !important;
  }
`

const Wrapper = styled(motion.div)`
  perspective: 1000px;
`

const Container = styled(motion.div)`
  display: flex;
  flex-direction: column;
  border: 1px solid hsl(var(--primary-800));
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.1s ease;
  min-height: 390px;
  overflow: hidden;
  transform-style: preserve-3d;

  &:hover {
    background-color: hsl(var(--primary-800));
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  height: 100px;
  border-bottom: 1px solid hsl(var(--primary-800));
  padding: 1rem;

  img {
    width: 70px;
    height: 70px;
    border-radius: 25%;
    margin-right: 1rem;
  }

  div {
    sub {
      text-transform: uppercase;
      color: #ff65b2;
      letter-spacing: 2px;
    }

    h1 {
      font-size: 18px;
      margin: 0;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    svg {
      width: 15px;
      height: 15px;
      color: #ccc;
    }

    span {
      color: #ccc;
    }
  }
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 1rem;
  box-sizing: border-box;
`

const Content = styled.div`
  flex: 1;
  line-height: 28px;
  font-weight: 500;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: auto;
`

const Button = styled(A)`
  z-index: 50;
  display: flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  background-color: hsl(var(--primary-800));
  transition: background-color 0.3s ease;

  &:hover {
    background-color: hsl(var(--primary-600));
  }

  svg {
    margin-right: 0.5rem;
  }
`

function Co({ id, url, name, iconReference, tagline, what, repoUrl }) {
  const [rotateX, setRotateX] = useState(0)
  const [rotateY, setRotateY] = useState(0)

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const centerX = rect.width / 2
    const centerY = rect.height / 2

    const rotateXValue = -(y - centerY) / 20
    const rotateYValue = (x - centerX) / 20

    setRotateX(rotateXValue)
    setRotateY(rotateYValue)
  }

  const handleMouseLeave = () => {
    setRotateX(0)
    setRotateY(0)
  }

  const handleClick = async ({ projectLink, projectId }) => {
    if (projectLink) {
      window.open(projectLink, '_blank', 'noopener,noreferrer')
      await addViews(projectId)
    }
  }

  return (
    <A onClick={() => handleClick({ projectLink: url, projectId: id })}>
      <Wrapper>
        <Container
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          animate={{
            rotateX,
            rotateY,
            scale: rotateX !== 0 || rotateY !== 0 ? 1.05 : 1,
          }}
          transition={{
            type: 'spring',
            stiffness: 350,
            damping: 40,
            mass: 2,
          }}
        >
          <Header>
            {iconReference
              ? (
                  <img alt={`${name}'s logo`} draggable={false} src={iconReference} />
                )
              : (
                  <Skeleton style={{ width: '70px', height: '70px', borderRadius: '25%', marginRight: '1rem' }} />
                )}

            <div>
              {name
                ? (
                    <h1>
                      {name}
                      {' '}
                      <FaExternalLinkAlt />
                    </h1>
                  )
                : (
                    <Skeleton style={{ width: '125px', height: '15px', borderRadius: '1px' }} />
                  )}
              {tagline
                ? (
                    <span>{tagline}</span>
                  )
                : (
                    <Skeleton style={{ width: '145px', height: '15px', borderRadius: '1px', marginTop: '1rem' }} />
                  )}
            </div>
          </Header>

          <Body>
            {what ? <Content><p>{what}</p></Content> : <Content><Skeleton style={{ width: '100%', height: '125px', borderRadius: '3px' }} /></Content>}

            {repoUrl
              ? (
                  <Footer>
                    <Button
                      onClick={() => window.open(repoUrl, '_blank', 'noopener,noreferrer')}
                      href={repoUrl}
                      target="_blank"
                      rel="noopener"
                    >
                      <FaGithub />
                      {' '}
                      GitHub Repo
                    </Button>
                  </Footer>
                )
              : null}
          </Body>
        </Container>
      </Wrapper>
    </A>
  )
}

export default function Comp() {
  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const { data } = await api.get('/projects')
      return data
    },
  })

  return (
    <>
      {isLoading
        ? [...Array.from({ length: 8 })].map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Co key={`num-${i}`} url={false} name={false} iconReference={false} tagline={false} repoUrl={false} what={false} />
          ))
        : projects.data.map(p => (
            <Co
              id={p.id}
              key={p.id}
              url={p.url}
              name={p.name}
              iconReference={p.iconReference}
              tagline={p.tagline}
              repoUrl={p.repoUrl}
              what={p.what}
            />
          ))}
    </>
  )
}
